import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import styles from './DrawerModalMobile.module.css';

interface DatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (date: Dayjs) => void;
  selectedDate?: Dayjs;
  title?: string;
}

const DAYS_OF_WEEK = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const MONTHS = [
  'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
  'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
];

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedDate,
  title = 'Chọn ngày giao dịch'
}) => {
  const [currentDate, setCurrentDate] = useState<Dayjs>(selectedDate || dayjs());
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (selectedDate) {
      setCurrentDate(selectedDate);
    }
  }, [selectedDate]);

  const getDaysInMonth = (date: Dayjs) => {
    const startOfMonth = date.startOf('month');
    const endOfMonth = date.endOf('month');
    const startOfWeek = startOfMonth.startOf('week');
    const endOfWeek = endOfMonth.endOf('week');
    
    const days: Dayjs[] = [];
    let current = startOfWeek;
    
    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }
    
    return days;
  };

  const handleDateClick = (date: Dayjs) => {
    onSelect(date);
    onClose();
  };

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const isToday = (date: Dayjs) => {
    return date.isSame(dayjs(), 'day');
  };

  const isSelected = (date: Dayjs) => {
    return selectedDate && date.isSame(selectedDate, 'day');
  };

  const isCurrentMonth = (date: Dayjs) => {
    return date.isSame(currentDate, 'month');
  };

  const isWeekend = (date: Dayjs) => {
    const day = date.day();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };

  const days = getDaysInMonth(currentDate);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="90%"
      centered
      className={styles.datePickerModal}
      zIndex={9999}
      bodyStyle={{
        padding: 0,
        borderRadius: '16px',
        overflow: 'hidden'
      }}
      maskStyle={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
    >
      <div className={styles.datePickerContainer}>
        {/* Header */}
        <div className={styles.datePickerHeader}>
          <div className={styles.datePickerTitle}>{title}</div>
        </div>

        {/* Month Navigation */}
        <div className={styles.datePickerNavigation}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={handlePrevMonth}
            className={styles.datePickerNavButton}
          />
          <div className={styles.datePickerMonthYear}>
            {`${MONTHS[currentDate.month()]}/${currentDate.year()}`}
          </div>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={handleNextMonth}
            className={styles.datePickerNavButton}
          />
        </div>

        {/* Days of Week Header */}
        <div className={styles.datePickerDaysHeader}>
          {DAYS_OF_WEEK.map((day, index) => (
            <div
              key={day}
              className={`${styles.datePickerDayHeader} ${
                index >= 5 ? styles.datePickerWeekend : ''
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className={styles.datePickerCalendar}>
          {days.map((date, index) => (
            <div
              key={index}
              className={`${styles.datePickerDay} ${
                !isCurrentMonth(date) ? styles.datePickerOtherMonth : ''
              } ${isWeekend(date) ? styles.datePickerWeekend : ''} ${
                isToday(date) ? styles.datePickerToday : ''
              } ${isSelected(date) ? styles.datePickerSelected : ''} ${
                hoveredDate?.isSame(date, 'day') ? styles.datePickerHovered : ''
              }`}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => setHoveredDate(date)}
              onMouseLeave={() => setHoveredDate(null)}
            >
              {date.date()}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
